import { AfterViewInit, Component, ContentChild, ContentChildren, Directive, Input, OnDestroy, OnInit, QueryList, ViewChild } from "@angular/core";
/** should be in shared */
import { Product } from "@brain/models";
import { BrainFilterDirective } from "@brain/modules/filter/ui/filter-control";
import { ApiService } from "@brain/services";
import { Observable, of, ReplaySubject, Subject } from "rxjs";
import { distinctUntilChanged, map, startWith, take, takeUntil } from "rxjs/operators";
import { BrainCatalogFilterComponent, BrainCatalogHideIfFilteredDirective } from "./filter";


/**
 * Directive which helps to slect an item which is rendered as header for the
 * catalog page
 *
 */
@Directive({
    selector: "[brainCatalogHeader]",
})
export class BrainCatalogHeaderDirective { }

/**
 * default store catalog component which are working with
 * composition
 *
 */
@Component({
    selector: "brain-catalog",
    templateUrl: "./catalog.html",
    styleUrls: ["./catalog.scss"]
})
export class BrainCatalogComponent implements AfterViewInit, OnInit, OnDestroy {

  /**
   * list of all available products which has
   * been loaded from datasource
   *
   */
  public availableProducts: Product[] = [];

  /**
   * list of products which are available after
   * they got filtered
   *
   */
  public filteredProducts: Product[] = [];

  /**
   * flag we are currently loading data from datasource
   *
   */
  public isLoading = false;

  /**
   *
   *
   */
  @ContentChild(BrainFilterDirective, {read: BrainFilterDirective, static: true})
  private filterControl: BrainFilterDirective;

  /**
   * internal stream which emits if new products (filtered products)
   * are available
   *
   */
  private products$: ReplaySubject<Product[]>;

  /**
   * helper for product groups, since we want to show products in
   * groups we need an internal pointer where to start
   *
   */
  private productListPointer = 0;

  /**
   * emits true as soon component gets destroyed to unsubscribe
   * and avoid memory leaks
   *
   */
  private destroy$: Subject<boolean> = new Subject();

  /**
   * flag we have set an datasource, otherwise it will take
   * the data from api service by catalog name
   *
   */
  private hasDataSource = false;

  /**
   * catalog name
   *
   */
  @Input()
  public name = "global";

  /**
   * filter stream to switch visiblity between hideIfFiltered Directives
   *
   */
  private filter$: Subject<boolean>;

  /**
   * get filter
   *
   */
  @ViewChild(BrainCatalogFilterComponent, { read: BrainCatalogFilterComponent, static: false })
  public set filterItem(filter: BrainCatalogFilterComponent) {

      if (!filter || this.hideOnFiltered.length === 0) {
          return;
      }

      this.filter = filter;

      if (!this.filter$) {
          this.toggleVisibilityIfFiltered();
      }
  }

  private filter: BrainCatalogFilterComponent;

  /**
   * get all items which should be hide when filter is active
   *
   */
  @ContentChildren(BrainCatalogHideIfFilteredDirective, {read: BrainCatalogHideIfFilteredDirective})
  private hideOnFiltered: QueryList<BrainCatalogHideIfFilteredDirective>;

  /**
   * current datasource for our products which should be shown in
   * catalog
   *
   */
  @Input()
  public set dataSource(products: Product[] | Observable<Product[]>) {
      const product$ = Array.isArray(products) ? of(products) : products;

      product$.pipe(take(1)).subscribe((items: Product[]) => {
          this.availableProducts = items;
          this.products$.next(items);
      });

      this.hasDataSource = true;
  }

  public constructor(
    private apiService: ApiService,
  ) {
      this.products$ = new ReplaySubject(1);
  }

  /**
   * component gets initialized, if no datasource has been passed
   * we load the data from api service by name
   *
   */
  public ngOnInit(): void {
      this.isLoading = true;

      /** if no datasource is set */
      if (!this.hasDataSource) {
          this.dataSource = this.apiService.getProducts(this.name);
      }

      this.products$
          .pipe(takeUntil(this.destroy$))
          .subscribe((products: Product[]) => {
              this.productListPointer = 0;
              this.filteredProducts  = [...products];
              this.isLoading = false;
          });
  }

  ngAfterViewInit(): void {
      this.filterControl.filterChanged
          .pipe(takeUntil(this.destroy$))
          .subscribe((products) => {
              this.products$.next(products || this.availableProducts);
              this.filter$?.next(!!products);
          });
  }

  /**
   * components gets destroyed, clean up everything
   *
   */
  public ngOnDestroy(): void {
      this.destroy$.next(true);
      this.destroy$.complete();
      this.products$.complete();

      this.destroy$  = null;
      this.products$ = null;
  }

  /**
   * resolve products from filtered list, if count is passed it will
   * take max n values from source otherwise it will return all or these
   * ones which are left
   *
   */
  public resolveProducts(count: number): Product[] {
      const extracted = this.filteredProducts.slice(
          this.productListPointer,
          count ? this.productListPointer + count : void 0
      );

      this.productListPointer += count;
      return extracted;
  }

  /**
   * get notified if new products has been loaded
   *
   */
  public productsChanged(): Observable<boolean> {
      return this.products$.pipe(map(() => true));
  }

  /**
   * filters has been changed
   *
   */
  public onFilterChange(products: Product[]): void {
      this.products$.next(products);
      this.filter$?.next(this.filter?.isFiltered ?? false);
  }

  /**
   * creeate stream to toggle visiblity for items which should be hidden
   * if filter is activated or shown if inactive
   *
   */
  private toggleVisibilityIfFiltered() {
      this.filter$ = new Subject();
      this.filter$.pipe(
      /** dont emit if filter state dosent change */
          distinctUntilChanged((filtered: boolean) => filtered === this.filter.isFiltered),
          /** start with default value */
          startWith(this.filter.isFiltered)
      ).subscribe((filtered) => this.hideOnFiltered.forEach((item) => filtered ? item.hide() : item.show()));
  }
}
